import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeTables } from '../lib/realtime';
import type { DrivingSession, DriverCompliance, ComplianceStatus } from '../lib/types';

// Local calendar day (local midnight) — MUST match the mobile app's
// startOfDay() so night-shift sessions land on the same day on both surfaces.
const pad2 = (n: number) => String(n).padStart(2, '0');
const localDayKey = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const weekAgoStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString();
};

function computeCompliance(sessions: DrivingSession[]): DriverCompliance {
  const today = localDayKey(new Date());

  const todaySessions = sessions.filter(s => localDayKey(new Date(s.started_at)) === today);
  const todayMinutes = todaySessions.reduce((sum, s) => sum + (s.driving_minutes ?? 0), 0);
  const todayBreaks = todaySessions.flatMap(s => s.breaks ?? []);
  const todayBreakMinutes = todayBreaks.reduce((sum, b) => {
    if (!b.ended_at) return sum;
    return sum + Math.round((new Date(b.ended_at).getTime() - new Date(b.started_at).getTime()) / 60000);
  }, 0);

  const maxSingleBreak = todayBreaks.reduce((max, b) => {
    if (!b.ended_at) return max;
    const mins = Math.round((new Date(b.ended_at).getTime() - new Date(b.started_at).getTime()) / 60000);
    return Math.max(max, mins);
  }, 0);

  const weekMinutes = sessions.reduce((sum, s) => sum + (s.driving_minutes ?? 0), 0);

  let status: ComplianceStatus = 'ok';
  if (todayMinutes > 780) status = 'violation';
  else if (todayMinutes > 540 && maxSingleBreak < 45) status = 'warning'; // >9h needs 45-min break
  else if (todayMinutes > 360 && maxSingleBreak < 30) status = 'warning'; // >6h needs 30-min break

  return { todayMinutes, weekMinutes, todaySessions: todaySessions.length, todayBreakMinutes, status };
}

export function useFleetCompliance() {
  const [compliance, setCompliance] = useState<Record<string, DriverCompliance>>({});
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    const { data } = await supabase
      .from('driving_sessions')
      .select('*, breaks(*)')
      .gte('started_at', weekAgoStr());

    const sessions = (data ?? []) as DrivingSession[];
    const grouped: Record<string, DrivingSession[]> = {};
    for (const s of sessions) {
      if (!grouped[s.driver_id]) grouped[s.driver_id] = [];
      grouped[s.driver_id].push(s);
    }
    const result: Record<string, DriverCompliance> = {};
    for (const [driverId, driverSessions] of Object.entries(grouped)) {
      result[driverId] = computeCompliance(driverSessions);
    }
    setCompliance(result);
    setLoading(false);
  }, []);

  useEffect(() => {
    refetch();
    const unsub = subscribeTables(['driving_sessions', 'breaks', 'trips'], refetch);
    return unsub;
  }, [refetch]);

  return { compliance, loading, refetch };
}
